import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {testIdProps} from '@github-ui/test-id-props'
import {ProjectTemplateIcon, TableIcon} from '@primer/octicons-react'
import {Button, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useMemo} from 'react'

import {NewProjectForm} from './NewProjectForm'
import {OpenClosedProjects} from './OpenClosedProjects'
import type {ProjectSortValue} from './project-sort-options'
import {ProjectRow} from './ProjectRow'
import {ProjectSortMenu} from './ProjectSortMenu'
import styles from './RepositoryProjects.module.css'
import type {NewProjectAction, Project} from './types'

interface ProjectListProps {
  projects: Project[]
  openCount: number
  closedCount: number
  hasNextPage: boolean
  loadingMore: boolean
  onLoadMore: () => void
  viewerCanPush: boolean
  /** Repo owner login, used as the form action prefix for the "New project" CTA. */
  owner: string
  /** Repo name, used as the form action prefix for the "New project" CTA. */
  repo: string
  /**
   * Hidden-field bundle for the "New project" CTA. `null` when the viewer can't
   * create a project here — the CTA is hidden in that case.
   */
  newProjectAction: NewProjectAction | null
  query: string
  /** The current open/closed filter state, used to tailor the empty-state copy. */
  state: 'open' | 'closed'
  sort: ProjectSortValue
  onSortChange: (value: ProjectSortValue) => void
  isRefetching?: boolean
  /**
   * When true, render a spinner inside the list area instead of the rows or blankslate,
   * keeping the toolbar visible during the cross-route remount when toggling between
   * the projects and templates views.
   */
  isLoading?: boolean
  /** When true, the list is showing the templates view; affects blankslate copy and icon. */
  isTemplateView?: boolean
}

export function ProjectList({
  projects,
  openCount,
  closedCount,
  hasNextPage,
  loadingMore,
  onLoadMore,
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  query,
  state,
  sort,
  onSortChange,
  isRefetching = false,
  isLoading = false,
  isTemplateView = false,
}: ProjectListProps) {
  const listItemsHeader = (
    <ListViewMetadata sectionFilters={<OpenClosedProjects openCount={openCount} closedCount={closedCount} />}>
      <ProjectSortMenu sort={sort} onSortChange={onSortChange} />
    </ListViewMetadata>
  )

  const loadMoreButton = useMemo(() => {
    return hasNextPage ? (
      <div className={styles.loadMoreButtonWrapper}>
        <Button
          variant="invisible"
          onClick={onLoadMore}
          className={styles.loadMoreButton}
          data-testid="load-more-projects-button"
          loading={loadingMore}
        >
          Load more projects
        </Button>
      </div>
    ) : null
  }, [hasNextPage, loadingMore, onLoadMore])

  const renderProjectList = () => {
    if (isLoading) {
      return (
        <div className={styles.loadingContainer} role="status">
          <Spinner size="large" />
        </div>
      )
    }

    if (projects.length > 0) {
      return projects.map(project => <ProjectRow key={project.id} project={project} />)
    }

    const hasActiveQuery = query.trim().length > 0
    if (hasActiveQuery) {
      return <NoMatchingProjectsBlankslate isTemplateView={isTemplateView} />
    }
    return isTemplateView ? (
      <EmptyRepoTemplatesBlankslate viewerCanPush={viewerCanPush} state={state} />
    ) : (
      <EmptyRepoProjectsBlankslate
        viewerCanPush={viewerCanPush}
        owner={owner}
        repo={repo}
        newProjectAction={newProjectAction}
        state={state}
      />
    )
  }

  return (
    <>
      <div
        className={styles.projectListWrapper}
        data-hpc
        {...testIdProps('project-list-wrapper')}
        aria-busy={isRefetching || isLoading}
      >
        <ListView
          {...testIdProps('repository-project-list-view')}
          title="Repository projects"
          metadata={listItemsHeader}
          titleHeaderTag="h2"
          singularUnits="project"
          pluralUnits="projects"
          as={projects.length > 0 ? 'ul' : 'div'}
          role={projects.length > 0 ? 'list' : 'status'}
        >
          {renderProjectList()}
        </ListView>
      </div>
      {loadMoreButton}
    </>
  )
}

function NoMatchingProjectsBlankslate({isTemplateView}: {isTemplateView: boolean}) {
  const Icon = isTemplateView ? ProjectTemplateIcon : TableIcon
  const label = isTemplateView ? 'project templates' : 'projects'
  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <Icon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No {label} matched your search</Blankslate.Heading>
        <Blankslate.Description>
          Try a different search term or clear the search to see all {label}.
        </Blankslate.Description>
      </Blankslate>
    </div>
  )
}

interface EmptyRepoProjectsBlankslateProps {
  viewerCanPush: boolean
  owner: string
  repo: string
  newProjectAction: NewProjectAction | null
  state: 'open' | 'closed'
}

/**
 * The closed branch suppresses the "New project" CTA: filter-driven emptiness should
 * not look like a brand-new repo prompting the viewer to create a project.
 */
function EmptyRepoProjectsBlankslate({
  viewerCanPush,
  owner,
  repo,
  newProjectAction,
  state,
}: EmptyRepoProjectsBlankslateProps) {
  if (state === 'closed') {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <TableIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>No closed projects</Blankslate.Heading>
        </Blankslate>
      </div>
    )
  }

  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <TableIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>No projects found</Blankslate.Heading>
        <Blankslate.Description>
          {viewerCanPush
            ? 'There are no projects linked to this repository yet.'
            : 'There are no projects linked to this repository.'}
        </Blankslate.Description>
        {newProjectAction && (
          <NewProjectForm owner={owner} repo={repo} action={newProjectAction}>
            <Blankslate.PrimaryAction type="submit">New project</Blankslate.PrimaryAction>
          </NewProjectForm>
        )}
      </Blankslate>
    </div>
  )
}

interface EmptyRepoTemplatesBlankslateProps {
  viewerCanPush: boolean
  state: 'open' | 'closed'
}

function EmptyRepoTemplatesBlankslate({viewerCanPush, state}: EmptyRepoTemplatesBlankslateProps) {
  if (state === 'closed') {
    return (
      <div className={styles.emptyState}>
        <Blankslate>
          <Blankslate.Visual>
            <ProjectTemplateIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>No closed project templates</Blankslate.Heading>
        </Blankslate>
      </div>
    )
  }

  return (
    <div className={styles.emptyState}>
      <Blankslate>
        <Blankslate.Visual>
          <ProjectTemplateIcon size="medium" />
        </Blankslate.Visual>
        <Blankslate.Heading>
          {viewerCanPush ? 'Provide quick access to relevant project templates' : 'No project templates'}
        </Blankslate.Heading>
        {viewerCanPush && (
          <Blankslate.Description>
            Add project templates from your organization to view them here.
          </Blankslate.Description>
        )}
      </Blankslate>
    </div>
  )
}
